import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Send, TestTube2, Paperclip, Users, Info, FileText, X, BookOpen, Trash2, Save } from "lucide-react";

// Read a File into a data URL — used by both the main PDF upload and the
// next-term survey PDF upload. Local copy to avoid a bespoke import; the
// same helper is duplicated in Social.jsx and WelcomePack.jsx.
function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { safeHtml } from "../../lib/safeHtml";
import RichTextEditor from "../../components/RichTextEditor";

const SESSIONS = {
  all: "All sessions",
  tue_am: "Tuesday AM (10–1)",
  tue_pm: "Tuesday PM (2–5)",
  fri_eve: "Friday Evening (6–8:30)",
};

const AUDIENCES = {
  current: "Current term students",
  past: "Past students (re-engagement)",
  all: "Everyone (current + past)",
};

const STUDENT_TYPES = {
  all: "New + Returning",
  new: "New students only",
  returner: "Returners only",
};

const DEFAULT_BODY = `<p>Hope you are all excited for the summer term. See the curriculum attached and see you soon.</p>`;

export default function BulkEmail() {
  const [searchParams] = useSearchParams();
  const initialAudience = searchParams.get("audience") || "current";
  const [subject, setSubject] = useState(initialAudience === "past" ? "We miss you — come paint with us again!" : "Summer term — see you soon!");
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY);
  const [sessionFilter, setSessionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // all | new | returner
  const [audience, setAudience] = useState(initialAudience);
  const [includeCurriculum, setIncludeCurriculum] = useState(true);
  const [includeWhatsapp, setIncludeWhatsapp] = useState(false);
  const [curriculumVariants, setCurriculumVariants] = useState([]);
  const [curriculumVariantId, setCurriculumVariantId] = useState("auto");
  const [settings, setSettings] = useState(null);
  const [students, setStudents] = useState([]);
  const [sending, setSending] = useState(false);
  const [pdf, setPdf] = useState(null); // { filename, base64 }
  // Session-date / time overrides — used to send *next-term marketing*
  // emails where the actual class dates + times aren't set yet in the
  // studio calendar. When both are empty, the send_bulk_email endpoint
  // falls back to whatever it can derive from settings.session_times.
  const [overrideDate, setOverrideDate] = useState(""); // YYYY-MM-DD
  const [omitTime, setOmitTime] = useState(false); // hide the class time in the rendered email
  const [showSurvey, setShowSurvey] = useState(false);
  // Editable template library — dropdown at the top of the compose panel
  // that seeds subject + body_html. Admins can save current draft as new
  // template, edit built-ins, or delete their own custom ones.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/bulk-email-templates");
      setTemplates(data);
    } catch (e) {
      // Non-fatal — picker just stays empty. Log for observability so a
      // real network error isn't silently masked (e.g. permission gap
      // after adding a new admin without the bulk-email perm).
      console.warn("[BulkEmail] Failed to load templates:", e?.response?.data?.detail || e?.message || e);
    }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const applyTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    setSelectedTemplateId(tplId);
    setSubject(tpl.subject || "");
    setBodyHtml(tpl.body_html || "");
    toast.success(`Loaded "${tpl.name}" — edit freely before sending`);
  };

  const saveAsNewTemplate = async () => {
    const name = prompt("Name this template (e.g. 'Autumn welcome'):", "");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/admin/bulk-email-templates", {
        name: name.trim(), subject, body_html: bodyHtml,
      });
      toast.success(`Saved "${data.name}" to the template library`);
      await loadTemplates();
      setSelectedTemplateId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveOverwriteCurrent = async () => {
    if (!selectedTemplateId) { toast.error("Pick a template to overwrite first"); return; }
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    if (!confirm(`Overwrite "${tpl?.name}" with the current subject + body? This updates it for everyone.`)) return;
    try {
      await api.put(`/admin/bulk-email-templates/${selectedTemplateId}`, { subject, body_html: bodyHtml });
      toast.success("Template updated ✓");
      await loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  useEffect(() => {
    api.get("/admin/settings").then(r => setSettings(r.data)).catch(() => {});
    api.get("/admin/students").then(r => setStudents(r.data)).catch(() => {});
    api.get("/admin/curriculum-variants").then(r => setCurriculumVariants(r.data || [])).catch(() => {});
  }, []);

  const onPdfUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please upload a PDF"); return; }
    // Guard against silent truncation by production ingress — anything over
    // ~6 MB (raw) tends to fail as ~8 MB base64 exceeds the default proxy
    // request-body limit. Ask the admin to trim before we waste their time.
    if (file.size > 6 * 1024 * 1024) {
      toast.error(`PDF is ${(file.size / (1024 * 1024)).toFixed(1)} MB — please compress under 6 MB (Preview → File → Reduce File Size, or ilovepdf.com/compress_pdf) or upload it as a curriculum variant instead.`);
      return;
    }
    const dataUrl = await fileToB64(file);
    const base64 = dataUrl.split(",")[1];
    setPdf({ filename: file.name, base64 });
    toast.success(`${file.name} attached — images will be inlined + PDF attached`);
  };
  const clearPdf = () => setPdf(null);

  const currentTerm = settings?.current_term || "Summer 2026";
  const audienceFilter = useMemo(() => {
    return students.filter((s) => {
      if (!s.email) return false;
      if (audience === "current" && s.term !== currentTerm) return false;
      if (audience === "past" && s.term === currentTerm) return false;
      if (typeFilter === "new" && (s.student_type || "new") !== "new") return false;
      if (typeFilter === "returner" && s.student_type !== "returner") return false;
      return true;
    });
  }, [students, audience, currentTerm, typeFilter]);
  const filtered = useMemo(() => sessionFilter === "all" ? audienceFilter : audienceFilter.filter(s => s.session_key === sessionFilter), [audienceFilter, sessionFilter]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/bulk-email/test", {
        subject, body_html: bodyHtml, session_key: sessionFilter === "all" ? "tue_am" : sessionFilter,
        include_curriculum: includeCurriculum,
        curriculum_variant_id: curriculumVariantId,
        include_whatsapp: includeWhatsapp,
        type_filter: typeFilter,
        pdf_base64: pdf?.base64, pdf_filename: pdf?.filename,
        override_date: overrideDate || null,
        omit_time: omitTime,
      });
      toast.success(`Test sent to your inbox (${data.sent} email).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSending(false); }
  };

  const sendAll = async () => {
    if (!confirm(`Send this email to ${filtered.length} ${audience === "past" ? "past " : ""}student${filtered.length === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/bulk-email/send", {
        subject, body_html: bodyHtml, session_filter: sessionFilter, audience,
        include_curriculum: includeCurriculum,
        curriculum_variant_id: curriculumVariantId,
        include_whatsapp: includeWhatsapp,
        type_filter: typeFilter,
        pdf_base64: pdf?.base64, pdf_filename: pdf?.filename,
        override_date: overrideDate || null,
        omit_time: omitTime,
      });
      if (data.failed?.length) {
        toast.warning(`Sent ${data.sent} of ${data.recipients}. ${data.failed.length} failed.`);
      } else {
        toast.success(`Sent to ${data.sent} student${data.sent === 1 ? "" : "s"} ✓`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-bulk-email-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">write once, reach everyone</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Bulk email</h1>
          <p className="text-sm text-[#6E6074] mt-1">Newsletter-styled reminders sent to your current students. Date/time are auto-filled per student's session. Venue is set in Settings.</p>
        </div>
        <button
          onClick={() => setShowSurvey(true)}
          className="sac-btn-secondary text-sm"
          data-testid="open-next-term-survey"
          title="Send Yes/Maybe/No survey to see who's returning next term"
        >
          Next-term interest survey…
        </button>
      </div>

      {showSurvey && (
        <NextTermSurveyModal
          settings={settings}
          onClose={() => setShowSurvey(false)}
          initialCurriculum={pdf}
        />
      )}

      {showTemplateManager && (
        <TemplateManagerModal
          templates={templates}
          onClose={() => setShowTemplateManager(false)}
          onChanged={loadTemplates}
        />
      )}

      <div className="grid lg:grid-cols-2 gap-6 min-w-0">
        {/* Composer */}
        <div className="admin-surface p-6 min-w-0">
          <h2 className="font-display text-xl text-[#5D2C8F] mb-4">Compose</h2>
          <div className="space-y-4">
            {/* Template picker — load subject + body from the library, or
                save the current draft as a new named template. Freely
                editable after loading — templates are just a starting point. */}
            <div className="bg-[#FAF6EE] border border-[#E5E0D8] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-[#5D2C8F] font-medium flex items-center gap-1.5"><BookOpen size={13} /> Start from a template</label>
                <button onClick={() => setShowTemplateManager(true)} className="text-[11px] text-[#5D2C8F] hover:underline" data-testid="bulk-manage-templates">Manage library ({templates.length})</button>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={selectedTemplateId}
                  onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                  className="flex-1 min-w-[220px] bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm"
                  data-testid="bulk-template-picker"
                >
                  <option value="">— Pick a template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.category === "seed" ? "★ " : ""}{t.name}</option>
                  ))}
                </select>
                <button onClick={saveAsNewTemplate} className="sac-btn-secondary text-xs" data-testid="bulk-save-new-template" title="Save current subject + body as a new template">
                  <Save size={12} /> Save as new
                </button>
                {selectedTemplateId && (
                  <button onClick={saveOverwriteCurrent} className="text-xs text-[#5D2C8F] hover:underline" data-testid="bulk-overwrite-template" title="Overwrite the selected template with the current subject + body">
                    Update selected
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[#6E6074] mt-1.5">Templates are just starting points — edit the subject and body freely below before sending. Merge tags like <code className="bg-white px-1 rounded text-[10px]">{`{{first_name}}`}</code> are substituted per recipient.</p>
            </div>

            <div>
              <label className="text-sm text-[#5D2C8F]">Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="bulk-subject" />
            </div>
            <div>
              <label className="text-sm text-[#5D2C8F]">Body</label>
              <p className="text-[11px] text-[#6E6074] mt-0.5 mb-1">Use the toolbar for formatting, links, and inline images. Date, Time and Venue are appended automatically for each recipient based on their session. Signature ends with "{settings?.signature_signoff || "Every Blessing"} · {settings?.signature_name || "Jadesola Ayoola"}".</p>
              <RichTextEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                placeholder="Hi everyone, here's what's happening this term…"
                testid="bulk-body"
                minHeight={260}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#5D2C8F]">Audience</label>
                <select value={audience} onChange={e => setAudience(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="bulk-audience">
                  {Object.entries(AUDIENCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#5D2C8F]">Session</label>
                <select value={sessionFilter} onChange={e => setSessionFilter(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="bulk-session-filter">
                  {Object.entries(SESSIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm text-[#5D2C8F]">Student type</label>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="bulk-type-filter">
                  {Object.entries(STUDENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#5D2C8F] self-end pb-2 col-span-2">
                <input type="checkbox" checked={includeCurriculum} onChange={e => setIncludeCurriculum(e.target.checked)} data-testid="bulk-include-curriculum" />
                <Paperclip size={14} /> Attach curriculum PDF
              </label>
              {includeCurriculum && (
                <div className="col-span-2" data-testid="bulk-curriculum-variant-row">
                  <label className="text-xs text-[#5D2C8F] block mb-1">Which curriculum?</label>
                  <select value={curriculumVariantId} onChange={(e) => setCurriculumVariantId(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="bulk-curriculum-variant">
                    <option value="auto">Auto — pick per student's session</option>
                    <option value="none">None (skip attachment)</option>
                    {curriculumVariants.length > 0 && <optgroup label="Force a specific curriculum">{curriculumVariants.map((v) => (<option key={v.id} value={v.id}>{v.label}{v.session_key ? ` · ${v.session_key === 'tue_am' ? 'Tue AM' : v.session_key === 'tue_pm' ? 'Tue PM' : 'Fri Eve'}` : ""}{v.is_default ? " · default" : ""}</option>))}</optgroup>}
                  </select>
                  <p className="text-[11px] text-[#6E6074] leading-snug mt-1">Auto attaches the matching per-session PDF (fallback: default). Upload variants in <strong>Welcome Pack → Curriculum PDFs</strong>.</p>
                </div>
              )}
              <label className="flex items-start gap-2 text-sm text-[#5D2C8F] pb-2 col-span-2" data-testid="bulk-include-whatsapp-row">
                <input type="checkbox" checked={includeWhatsapp} onChange={e => setIncludeWhatsapp(e.target.checked)} className="mt-1" data-testid="bulk-include-whatsapp" />
                <div>
                  <span className="flex items-center gap-1 font-medium"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M12 0C5.37 0 0 5.37 0 12c0 2.12.55 4.11 1.52 5.85L0 24l6.35-1.66A12 12 0 0 0 12 24c6.63 0 12-5.37 12-12S18.63 0 12 0zm0 21.6a9.6 9.6 0 0 1-4.88-1.33l-.35-.2-3.78.99 1-3.67-.23-.38A9.6 9.6 0 1 1 12 21.6z"/></svg> Include WhatsApp group link</span>
                  <span className="text-[11px] text-[#6E6074] block leading-snug">Pins your session's group invite button to the bottom of the email. <strong>Only sent to NEW students</strong> — returners are skipped. Set each group's link in Settings → Venue &amp; Signature.</span>
                </div>
              </label>
            </div>

            {/* Optional PDF attach — extracts inline images + attaches the PDF */}
            <div className="pt-3 border-t border-[#E5E0D8]">
              <div className="text-sm text-[#5D2C8F] flex items-center gap-2 mb-2"><FileText size={14} /> Attach a PDF to this email</div>
              {pdf ? (
                <div className="flex items-center justify-between bg-[#FAF6EE] rounded-lg px-3 py-2 text-sm">
                  <div className="text-[#3A2A47] truncate">{pdf.filename}</div>
                  <button onClick={clearPdf} className="text-[#A685BD]" data-testid="bulk-pdf-clear"><X size={14} /></button>
                </div>
              ) : (
                <label className="sac-btn-secondary cursor-pointer !py-2 !px-4 text-sm inline-flex">
                  <FileText size={14} /> Upload PDF
                  <input type="file" accept="application/pdf" className="hidden" onChange={(e) => onPdfUpload(e.target.files?.[0])} data-testid="bulk-pdf-upload" />
                </label>
              )}
              <div className="text-[11px] text-[#6E6074] mt-1">The PDF will be attached AND its first 9 images embedded inline in the email for a richer look.</div>
            </div>

            {/* Session date/time overrides — used when composing marketing emails
                for a term that hasn't started yet (e.g. "Autumn kicks off in Sept").
                Empty date = keep the default derived from settings.session_times. */}
            <div className="pt-3 border-t border-[#E5E0D8]">
              <div className="text-sm text-[#5D2C8F] flex items-center gap-2 mb-2"><Paperclip size={14} /> Session date &amp; time in email</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#5D2C8F] block mb-1">Override start date <span className="text-[#A685BD]">(optional)</span></label>
                  <input type="date" value={overrideDate} onChange={(e) => setOverrideDate(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="bulk-override-date" />
                  <p className="text-[11px] text-[#6E6074] mt-1">Blank = use whatever's set in Settings → Session times.</p>
                </div>
                <label className="flex items-start gap-2 text-sm text-[#5D2C8F] self-end pb-2" data-testid="bulk-omit-time-label">
                  <input type="checkbox" checked={omitTime} onChange={(e) => setOmitTime(e.target.checked)} className="mt-1" data-testid="bulk-omit-time" />
                  <div>
                    <span className="font-medium">Hide the class time</span>
                    <span className="text-[11px] text-[#6E6074] block leading-snug">Tick this for next-term marketing when the time isn't finalised — the email will say the date only.</span>
                  </div>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#E5E0D8] mt-4">
              <div className="text-sm text-[#5D2C8F] flex items-center gap-1"><Users size={14} /> <strong>{filtered.length}</strong> recipient{filtered.length === 1 ? "" : "s"}</div>
              <div className="flex gap-2">
                <button onClick={sendTest} disabled={sending} className="sac-btn-secondary text-sm" data-testid="bulk-test-btn"><TestTube2 size={14} /> Send test to me</button>
                <button onClick={sendAll} disabled={sending || filtered.length === 0} className="sac-btn-primary text-sm" data-testid="bulk-send-btn"><Send size={14} /> Send to {filtered.length}</button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview — pixel-mirrors the delivered email. Every rule is
              inline (matches _wrap() in email_templates.py) because Gmail
              strips the <style> block, so the preview must apply the
              SAME inline properties or it drifts from what recipients
              actually see. Container spec (from user):
                max-width:600px; margin:0 auto; padding:20px; width:100%;
                font-family:Arial,sans-serif; line-height:1.5; text-align:left;
                word-wrap:break-word; overflow-wrap:break-word; word-break:normal;
              `min-w-0` on the grid cell lets the flex/grid parent shrink
              below 600px on mobile. */}
        <div className="admin-surface p-6 min-w-0 overflow-x-hidden">
          <h2 className="font-display text-xl text-[#5D2C8F] mb-4">Preview</h2>
          <div className="rounded-lg border border-[#E5E0D8] bg-[#F1EAE0] p-3 overflow-x-hidden">
            <div
              className="[&_img]:max-w-full [&_img]:h-auto [&_img]:block"
              style={{
                // Container spec — EXACTLY what the delivered email uses.
                maxWidth: 600,
                width: "100%",
                margin: "0 auto",
                padding: 20,
                background: "#FAF6EE",
                borderRadius: 8,
                fontFamily: "Arial, Helvetica, sans-serif",
                lineHeight: 1.5,
                textAlign: "left",
                color: "#3A2A47",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                wordBreak: "normal",
                boxSizing: "border-box",
              }}
              data-testid="bulk-email-preview"
            >
              <div style={{ background: "#8EC9C7", padding: "20px", borderRadius: "4px 4px 0 0", margin: "-20px -20px 20px -20px" }}>
                <div style={{ color: "#5D2C8F", fontSize: 26, fontFamily: "Arial, Helvetica, sans-serif", fontWeight: 700, lineHeight: 1.3 }}>{subject}</div>
              </div>
              <p style={{ margin: "0 0 12px 0" }}>Hi [First Name],</p>
              <div
                data-testid="bulk-email-preview-body"
                style={{ fontFamily: "Arial, Helvetica, sans-serif", lineHeight: 1.5, textAlign: "left", wordWrap: "break-word", overflowWrap: "break-word", wordBreak: "normal" }}
                dangerouslySetInnerHTML={safeHtml(
                  // Match the backend `_strip_highlights` normalizer: replace
                  // Quill's clipboard-injected non-breaking spaces with real
                  // spaces so the preview wraps the same way Gmail does. See
                  // the comment on _strip_highlights() for the full rationale.
                  (bodyHtml || "")
                    .replace(/&nbsp;/g, " ")
                    .replace(/&#160;/g, " ")
                    .replace(/&#xa0;/gi, " ")
                    .replace(/\u00a0/g, " ")
                )}
              />
              <table style={{ width: "100%", fontSize: 14, marginTop: 16, borderTop: "1px solid #E8E2D5", borderBottom: "1px solid #E8E2D5", borderCollapse: "collapse" }}>
                <tbody>
                  <tr><td style={{ padding: "6px 0", color: "#7D6096", width: 88 }}>Date</td><td style={{ padding: "6px 0" }}>(auto by session — e.g. Tuesday, 28 April 2026)</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "#7D6096" }}>Time</td><td style={{ padding: "6px 0" }}>(auto by session — e.g. 9:30 AM – 12:30 PM)</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "#7D6096" }}>Venue</td><td style={{ padding: "6px 0" }}>{settings?.venue_address || "—"}</td></tr>
                </tbody>
              </table>
              <p style={{ marginTop: 24 }}>{settings?.signature_signoff || "Every Blessing"}<br /><strong>{settings?.signature_name || "Jadesola Ayoola"}</strong></p>
              <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #E5E0D8", fontSize: 12, color: "#7D6096", textAlign: "center" }}>
                The Seasons Art Class — East Croydon
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Next-term interest survey modal.
 * Sends a Yes/Maybe/No + Unsubscribe email to every current & past student
 * (filterable). Clicking Yes creates a lead + copies student to target term.
 * Clicking Maybe creates a lead with distinct 'returner_maybe' status.
 * Clicking No silently suppresses future next-term marketing.
 * Unsubscribe soft-flags the student — accounting history stays intact.
 */
function NextTermSurveyModal({ settings, onClose, initialCurriculum }) {
  const [targetTerm, setTargetTerm] = useState("");
  const [terms, setTerms] = useState([]);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState(
    `<p>The current term is winding down and we're already dreaming up what comes next — we'd love to hold a seat for you.</p><p>Attached is a preview of what we'll be exploring: watercolour, oil pastel, drawing, and acrylic — a fresh 14-week journey.</p><p>Have a click of the buttons below to let us know if you'd like to join us.</p>`
  );
  const [surveyPdf, setSurveyPdf] = useState(initialCurriculum || null);
  const [filterTerms, setFilterTerms] = useState([]); // empty = all
  const [excludeUnsub, setExcludeUnsub] = useState(true);
  const [preview, setPreview] = useState(null); // dryrun result
  const [sending, setSending] = useState(false);
  const [availableTerms, setAvailableTerms] = useState([]);

  useEffect(() => {
    api.get("/admin/terms").then((r) => setTerms(r.data || [])).catch(() => setTerms([]));
    // Distinct term labels currently used on student rows — the "filter recipients by past term" list
    api.get("/admin/students").then((r) => {
      const seen = new Set((r.data || []).map((s) => s.term).filter(Boolean));
      setAvailableTerms([...seen].sort().reverse());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (targetTerm && !subject) setSubject(`Are you joining us for ${targetTerm}? · The Seasons Art Class`);
  }, [targetTerm, subject]);

  const onPdfChange = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please upload a PDF"); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("PDF too large — please compress under 6 MB"); return; }
    const dataUrl = await fileToB64(file);
    setSurveyPdf({ filename: file.name, base64: dataUrl.split(",")[1] });
  };

  const runDryRun = async () => {
    if (!targetTerm.trim()) { toast.error("Enter the target term first (e.g. Autumn 2026)"); return; }
    try {
      const { data } = await api.post("/admin/campaigns/next-term-survey", {
        target_term: targetTerm, subject, body_html: bodyHtml,
        filter_terms: filterTerms, exclude_unsubscribed: excludeUnsub, dry_run: true,
      });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const send = async () => {
    if (!targetTerm.trim()) { toast.error("Enter the target term first"); return; }
    if (!preview) { await runDryRun(); return; }
    if (!confirm(`Send the survey to ${preview.would_send_to} student${preview.would_send_to === 1 ? "" : "s"}? They'll each get 3 buttons (Yes / Maybe / No) + an Unsubscribe link.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns/next-term-survey", {
        target_term: targetTerm, subject, body_html: bodyHtml,
        filter_terms: filterTerms, exclude_unsubscribed: excludeUnsub,
        pdf_base64: surveyPdf?.base64, pdf_filename: surveyPdf?.filename,
      });
      toast.success(`Sent to ${data.sent} student${data.sent === 1 ? "" : "s"} · returners will populate the Leads page as replies come in ✓`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="admin-surface w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="next-term-survey-modal">
        <div className="px-6 py-4 border-b border-[#E5E0D8] flex items-start justify-between gap-3">
          <div>
            <div className="font-hand text-[#A685BD] text-sm">next-term marketing</div>
            <h3 className="font-display text-2xl text-[#5D2C8F]">Send Yes / Maybe / No survey</h3>
            <p className="text-xs text-[#6E6074] mt-1">Each recipient gets three big buttons + an unsubscribe link. "Yes" adds them to your <strong>returners lead list</strong> for the target term.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white" data-testid="survey-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-[#5D2C8F]">Target term</label>
              <input value={targetTerm} onChange={(e) => setTargetTerm(e.target.value)} placeholder="e.g. Autumn 2026" list="terms-datalist" className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="survey-target-term" />
              <datalist id="terms-datalist">{terms.map((t) => <option key={t.id || t.label} value={t.label} />)}</datalist>
            </div>
            <div>
              <label className="text-sm text-[#5D2C8F]">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="survey-subject" />
            </div>
          </div>

          <div>
            <label className="text-sm text-[#5D2C8F]">Message above the buttons</label>
            <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Hi ..." testid="survey-body" minHeight={180} />
          </div>

          <div>
            <label className="text-sm text-[#5D2C8F]">Attach curriculum PDF <span className="text-[#A685BD] text-xs">(optional but recommended)</span></label>
            <input type="file" accept="application/pdf" onChange={(e) => onPdfChange(e.target.files?.[0])} className="mt-1 text-xs" data-testid="survey-pdf-upload" />
            {surveyPdf && <div className="text-xs text-[#5D2C8F] mt-1">📎 {surveyPdf.filename}</div>}
          </div>

          <div className="bg-[#FAF6EE] border border-[#E5E0D8] rounded-lg p-3">
            <div className="text-sm text-[#5D2C8F] mb-2">Recipients</div>
            <p className="text-[11px] text-[#6E6074] mb-2">Empty = every student ever (excluding unsubscribed). Tick specific past terms to narrow the audience.</p>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {availableTerms.map((t) => (
                <label key={t} className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer ${filterTerms.includes(t) ? "bg-[#5D2C8F] text-white border-[#5D2C8F]" : "bg-white border-[#E5E0D8] text-[#5D2C8F]"}`}>
                  <input type="checkbox" checked={filterTerms.includes(t)} onChange={() => setFilterTerms((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t])} className="hidden" data-testid={`survey-term-${t.replace(/\s+/g, '-')}`} />
                  {t}
                </label>
              ))}
              {availableTerms.length === 0 && <span className="text-xs text-[#A685BD]">Loading terms…</span>}
            </div>
            <label className="text-xs text-[#6E6074] flex items-center gap-1.5 mt-2">
              <input type="checkbox" checked={excludeUnsub} onChange={(e) => setExcludeUnsub(e.target.checked)} data-testid="survey-exclude-unsub" />
              Exclude previously unsubscribed
            </label>
          </div>

          {preview && (
            <div className="bg-[#8EC9C7]/15 border border-[#8EC9C7]/40 rounded-lg p-3 text-sm text-[#5D2C8F]" data-testid="survey-preview">
              About to email <strong>{preview.would_send_to}</strong> student{preview.would_send_to === 1 ? "" : "s"}.
              {preview.sample?.length > 0 && <span className="block text-xs text-[#6E6074] mt-1">Sample: {preview.sample.join(", ")}…</span>}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E5E0D8] flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="survey-cancel">Cancel</button>
          <div className="flex gap-2">
            <button onClick={runDryRun} className="sac-btn-secondary text-sm" data-testid="survey-preview-btn">Preview count</button>
            <button onClick={send} disabled={sending || !targetTerm.trim()} className="sac-btn-primary disabled:opacity-50" data-testid="survey-send-btn">
              <Send size={14} /> {sending ? "Sending…" : (preview ? `Send to ${preview.would_send_to}` : "Send survey")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Manage the bulk-email template library.
 * - Built-in ("seed") templates can be renamed/edited but not deleted.
 * - Custom templates (created via "Save as new") can be edited AND deleted.
 * - All fields are editable inline; changes save on blur.
 */
function TemplateManagerModal({ templates, onClose, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", subject: "", body_html: "" });
  const [busy, setBusy] = useState(false);

  const startEdit = (t) => { setEditingId(t.id); setDraft({ name: t.name, subject: t.subject || "", body_html: t.body_html || "" }); };
  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/bulk-email-templates/${editingId}`, draft);
      toast.success("Saved");
      setEditingId(null);
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const del = async (t) => {
    if (!confirm(`Delete "${t.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/bulk-email-templates/${t.id}`);
      toast.success("Deleted");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="admin-surface w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="template-manager-modal">
        <div className="px-6 py-4 border-b border-[#E5E0D8] flex items-start justify-between gap-3">
          <div>
            <div className="font-hand text-[#A685BD] text-sm">templates</div>
            <h3 className="font-display text-2xl text-[#5D2C8F]">Bulk-email template library</h3>
            <p className="text-xs text-[#6E6074] mt-1">Built-ins (★) are editable but can't be deleted. Custom templates are fully editable and removable.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white" data-testid="template-manager-close"><X size={18} /></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="bg-white border border-[#E5E0D8] rounded-lg p-3" data-testid={`tpl-row-${t.id}`}>
              {editingId === t.id ? (
                <div className="space-y-2">
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1 text-sm" placeholder="Name" data-testid="tpl-edit-name" />
                  <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1 text-sm" placeholder="Subject" data-testid="tpl-edit-subject" />
                  <textarea value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} rows={6} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1 text-xs font-mono" placeholder="Body HTML" data-testid="tpl-edit-body" />
                  <div className="flex gap-2">
                    <button onClick={save} disabled={busy} className="sac-btn-primary text-xs disabled:opacity-50" data-testid="tpl-edit-save">
                      <Save size={11} /> {busy ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-[#6E6074]" data-testid="tpl-edit-cancel">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#5D2C8F] flex items-center gap-2">
                      {t.category === "seed" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E5E0D8] text-[#6E6074] uppercase tracking-widest">Built-in</span>}
                      {t.name}
                    </div>
                    <div className="text-xs text-[#6E6074] mt-0.5 line-clamp-1"><strong>Subject:</strong> {t.subject || "(none)"}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => startEdit(t)} className="text-xs text-[#5D2C8F] hover:underline" data-testid={`tpl-edit-btn-${t.id}`}>Edit</button>
                    {t.category !== "seed" && (
                      <button onClick={() => del(t)} className="p-1 text-[#A685BD]" title="Delete" data-testid={`tpl-delete-${t.id}`}><Trash2 size={12} /></button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {templates.length === 0 && (
            <div className="text-center text-sm text-[#6E6074] py-6">No templates yet — they'll auto-seed on the next page load.</div>
          )}
        </div>
      </div>
    </div>
  );
}

